import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface AgendaConfig {
  instructor_id:    string;
  working_days:     string[];
  working_hours:    Record<string, { start: string; end: string }>;
  default_duration: number;
  is_public:        boolean;
  class_value:      number;
  currency:         string;
  created_at?:      string;
  updated_at?:      string;
}

const DEFAULT_WORKING_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];
const DEFAULT_WORKING_HOURS: Record<string, { start: string; end: string }> = {
  mon: { start: '08:00', end: '18:00' },
  tue: { start: '08:00', end: '18:00' },
  wed: { start: '08:00', end: '18:00' },
  thu: { start: '08:00', end: '18:00' },
  fri: { start: '08:00', end: '18:00' },
};

export const useAgendaConfig = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: config, isLoading, error, refetch } = useQuery({
    queryKey: ['agenda-config', user?.id],
    queryFn: async (): Promise<AgendaConfig> => {
      if (!user) throw new Error('No user');

      const [configResult, intervalsResult] = await Promise.all([
        supabase.rpc('get_instructor_agenda_config_safe', {
          target_instructor_id: user.id
        }),
        supabase
          .from('instructor_schedule_intervals')
          .select('day_of_week, start_time, end_time')
          .eq('instructor_id', user.id)
          .order('start_time'),
      ]);

      if (configResult.error) throw configResult.error;

      // Build working_days and working_hours from schedule intervals
      const working_days: string[] = [];
      const working_hours: Record<string, { start: string; end: string }> = {};

      if (intervalsResult.data && intervalsResult.data.length > 0) {
        for (const interval of intervalsResult.data) {
          if (!working_days.includes(interval.day_of_week)) {
            working_days.push(interval.day_of_week);
          }
          if (!working_hours[interval.day_of_week]) {
            working_hours[interval.day_of_week] = {
              start: interval.start_time.slice(0, 5),
              end: interval.end_time.slice(0, 5),
            };
          } else {
            // Multiple intervals on the same day: extend the end to the latest end_time
            working_hours[interval.day_of_week].end = interval.end_time.slice(0, 5);
          }
        }
      }

      const effectiveWorkingDays = working_days.length > 0 ? working_days : DEFAULT_WORKING_DAYS;
      const effectiveWorkingHours = working_days.length > 0 ? working_hours : DEFAULT_WORKING_HOURS;

      const { data } = configResult;

      if (!data || (Array.isArray(data) && data.length === 0)) {
        return {
          instructor_id:    user.id,
          working_days:     effectiveWorkingDays,
          working_hours:    effectiveWorkingHours,
          default_duration: 60,
          is_public:        false,
          class_value:      0.00,
          currency:         'USD',
        };
      }

      const configData = Array.isArray(data) && data.length > 0 ? data[0] : data;
      return {
        ...configData,
        working_days:  effectiveWorkingDays,
        working_hours: effectiveWorkingHours,
      };
    },
    enabled: !!user,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const updateConfigMutation = useMutation({
    mutationFn: async (newConfig: Partial<AgendaConfig>) => {
      if (!user) throw new Error('No user');

      const configData = {
        instructor_id: user.id,
        ...newConfig,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('instructor_agenda_config')
        .upsert(configData, { onConflict: 'instructor_id' });

      if (error) throw error;
      return configData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agenda-config', user?.id] });
    },
  });

  return {
    config,
    isLoading,
    error: error?.message || null,
    updateConfig: updateConfigMutation.mutateAsync,
    isUpdating: updateConfigMutation.isPending,
    refetch
  };
};
